import { combineReducers, createStore, applyMiddleware, compose } from 'redux'
import { persistStore, persistReducer } from 'redux-persist'
import { createLogger } from 'redux-logger'
import storage from 'redux-persist/lib/storage'
import immutableTransform from 'config/immutableTransForm'
import tokenRefresh from 'modules/tokenRefresh/reducers'
import help from 'modules/help/reducers'
import helpCenter from 'modules/helpCenter/reducers'
import contactUs from 'modules/contactUs/reducers'
import subscription from 'modules/subscription/reducers'
import content from 'modules/content/reducers'
import basket from 'modules/basket/reducers'
import rootSaga, { sagaMiddleware } from '../sagas'
import authentication from '../../modules/authentication/reducers'
import languageChange from '../../modules/languageChange/reducers'
import appSettings from '../../modules/appSettings/reducers'
import configuration from '../../modules/configuration/reducers'
import location from '../../modules/location/reducers'
import user from '../../modules/user/reducers'
import products from '../../modules/products/reducers'
import wallet from '../../modules/wallet/reducers'
import orders from '../../modules/orders/reducers'
import catering from '../../modules/catering/reducers'
import { environment } from '../environment'
import { analyticsMiddleware } from '../../middleware/index'
import { handleLogout } from '../../utils/authUtils'
import { util } from 'utils'
import { getAppVersion } from 'utils/appConfiguration'

let navigateRef = null // Global variable to store navigate
const VERSION_KEY = 'persisted_app_version'

export const setNavigate = navigate => {
    navigateRef = navigate // Function to set navigate
}

const errorMiddleware = store => next => async action => {
    if (action?.meta?.errorStatus) {
        const { errorStatus } = action.meta

        // 401 - Token expired (Unauthorised) - We need get refrehsed token from server
        if (errorStatus === 401) {
            const { tokenRefresh } = store.getState()
            const isRefreshingToken = tokenRefresh.toJS().refreshingToken

            if (action.type === 'REFRESH_TOKEN' && isRefreshingToken) {
                return
            }
            if (action.type === 'GET_MFA_AUTH_FAILED') {
                store.dispatch({ type: 'GET_MFA_AUTH_FAILED', payload: action.payload })
                return
            }
            if (action.type === 'GET_MFA_RESEND_CODE_FAILED') {
                store.dispatch({ type: 'GET_MFA_RESEND_CODE_FAILED', payload: action.payload })
                return
            }
            if (action.type !== 'REFRESH_TOKEN' && !isRefreshingToken) {
                store.dispatch({ type: 'REFRESH_TOKEN' })
            }
            return
        }

        // 403 - Access removed for Logged in user - Logout user on 403
        if (errorStatus === 403) {
            const state = store.getState()
            const token = state.appSettings?.get('jwt')
            const { isSignedIn } = util.getJWTDetails()
            
            // 'Establishing connection with Android Interface to close the Web View',
            if (window.androidDeliveryJSBridge && isSignedIn) {
                window.androidDeliveryJSBridge.forceLogoutPostMerge()
            }

            // 'Establishing connection with iOS Interface to close the Web View',
            if (window.webkit?.messageHandlers?.deeplink && isSignedIn) {
                window.webkit.messageHandlers.deeplink.postMessage('forceLogoutPostMerge')
            }

            if (navigateRef) {
                await handleLogout(store.dispatch, navigateRef, token)
            }

            // need to refresh the app since any pop-ups OR sidebar can be in open state & we can't identify what to close & what to now
            // Delay the reload by 1 second to allow cleanup
            setTimeout(() => {
                window.location.reload()
            }, 1000)

            return
        }
    }

    next(action)
}

//To clear the redux persisted data if there is any change in redux persist
const purgePersistedState = async (persistor) => {
    const savedVersion = localStorage.getItem(VERSION_KEY)
    if (savedVersion !== getAppVersion()) {
      console.log(`[PERSIST] App version changed from ${savedVersion} to ${getAppVersion()}. Purging is needed`)
      await persistor.purge()
      localStorage.setItem(VERSION_KEY, getAppVersion())
    } else {
      console.log('[PERSIST] App version is current. No purge needed.')
    }
  }

const persistConfig = {
    key: 'root',
    storage,
    transforms: [immutableTransform],
    whitelist: ['user', 'appSettings', 'configuration', 'location', 'wallet'], //All the reducers that we want to persist goes here
    timeout: 0,
}

const productsConfig = {
    key: 'products',
    storage,
    whitelist: ['searchedValue', 'selectedSubCategory'], // Removed 'homePageContent' from persist
}

const rootReducer = combineReducers({
    authentication,
    languageChange,
    appSettings,
    configuration,
    contactUs,
    help,
    location,
    user,
    basket,
    wallet,
    tokenRefresh,
    products: persistReducer(productsConfig, products),
    orders,
    helpCenter,
    subscription,
    content,
    catering,
})

const persistedReducer = persistReducer(persistConfig, rootReducer)

const logger = createLogger({
    collapsed: true,
})

const middlewareList = [sagaMiddleware, errorMiddleware, analyticsMiddleware]
environment === 'development' && middlewareList.push(logger)

let composeEnhancers =
    environment === 'development' || environment === 'qa'
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
        : compose

if (composeEnhancers) {
    composeEnhancers = composeEnhancers(applyMiddleware(...middlewareList))
} else {
    composeEnhancers = applyMiddleware(...middlewareList)
}

 const store = createStore(persistedReducer, composeEnhancers)
if (environment === 'development') {
    window.store = store
}

 const persistor = persistStore(store)

sagaMiddleware.run(rootSaga)

// Call version check & purge
purgePersistedState(persistor)

export { store, persistor }